import type { LocationQueryValue } from 'vue-router'

import type { ScratchcardTeaserPayload } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { KnownProductType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import { QUERY_PRICES } from '~/constants/queryParams'
import { useFetchOptions } from '~/custom-pages/home/utils/fetchOptions'

const ascending = (a: number, b: number) => a - b

const isNumber = (val: number) => !isNaN(val)

const parseQueryPrices = (queryValue: LocationQueryValue | LocationQueryValue[]) =>
  (Array.isArray(queryValue) ? queryValue : [queryValue])
    .filter((value) => value !== null && value !== undefined)
    .map(Number)
    .filter(isNumber)

const toPrice = (acc: number[], teaser: ScratchcardTeaserPayload) => {
  const newPrice = teaser.price?.value
  if (!newPrice || acc.includes(newPrice)) return acc

  acc.push(newPrice)
  return acc
}

export const useScratchcardFilter = (options: { eager?: boolean; syncQuery?: boolean } = {}) => {
  const router = useRouter()
  const licensedTerritory = useLicensedTerritory({ raw: true })

  const filters = useState<number[]>(() => [])

  const isScratchRoute = computed(() => router.currentRoute.value.path.includes('/scratch'))

  const enabled = options.eager ? true : isScratchRoute

  const { loading, result } = useProductCatalogQuery(
    { licensedTerritory, productType: KnownProductType.Scratchcard },
    {
      enabled,
      ...useFetchOptions(),
    },
  )

  const teasers = computed<ScratchcardTeaserPayload[]>(() => {
    const productCatalog = result.value?.productCatalog
    if (!productCatalog || !('elements' in productCatalog)) return []

    return (productCatalog.elements ?? []) as ScratchcardTeaserPayload[]
  })

  const prices = computed(() => teasers.value.reduce(toPrice, []).sort(ascending))

  const hasCatalog = computed(() => !!result.value?.productCatalog)

  const activePrices = computed(() =>
    filters.value.filter((filter) => prices.value.includes(filter)).sort(ascending),
  )

  const filteredTeasers = computed(() => {
    if (activePrices.value.length <= 0) return teasers.value

    return teasers.value.filter(
      (teaser) => !!teaser.price && activePrices.value.includes(teaser.price.value),
    )
  })

  const setFilter = (price: number) => {
    const filterSet = new Set(filters.value)

    if (filterSet.has(price)) {
      filterSet.delete(price)
    } else {
      filterSet.add(price)
    }

    filters.value = Array.from(filterSet)
  }

  const updateQueryParams = async (prices: number[]) => {
    if (!isScratchRoute.value) return

    await router.replace({
      query: {
        ...router.currentRoute.value.query,
        [QUERY_PRICES]: prices.length >= 1 ? prices : undefined,
      },
    })
  }

  if (import.meta.client && options.syncQuery) {
    watch(
      [activePrices, loading, hasCatalog],
      ([active, isLoading, catalogLoaded]) => {
        if (!isScratchRoute.value || isLoading || !catalogLoaded) return

        const queryPrices = parseQueryPrices(router.currentRoute.value.query[QUERY_PRICES])

        if (
          queryPrices.length === active.length &&
          queryPrices.every((price, index) => price === active[index])
        ) {
          return
        }

        void updateQueryParams(active)
      },
      { immediate: true },
    )
  }

  onServerPrefetch(() => {
    const queryFilters = router.currentRoute.value.query[QUERY_PRICES]
    if (!queryFilters) return

    filters.value = parseQueryPrices(queryFilters)
  })

  return {
    activePrices: readonly(activePrices),
    filteredTeasers: readonly(filteredTeasers),
    loading,
    prices: readonly(prices),
    setFilter,
  }
}
